import React from 'react';
import { Form, Input, InputNumber, Radio, Select, Space, Tag } from 'antd';
import { useTranslation } from '../../i18n';

/*
 * Security group rule editor fields + helpers, shared by the user and admin pages.
 * family: both (双栈) | ipv4 | ipv6. "icmp" = ICMP of the rule's family; "icmpv6" is IPv6 only.
 * Form values: direction, protocol, family, source (any|any4|any6|custom), cidr, port_start, port_end, action.
 */

const PORTLESS = ['icmp', 'icmpv6', 'all'];
const ANY = { both: 'any', ipv4: 'any4', ipv6: 'any6' };

export const RULE_DEFAULTS = { direction: 'ingress', protocol: 'tcp', family: 'both', source: 'any', action: 'accept' };

/** Form values -> API payload. */
export const rulePayload = (v) => {
    const portless = PORTLESS.includes(v.protocol);
    const family = v.protocol === 'icmpv6' ? 'ipv6' : (v.family || 'both');
    const remote = v.source === 'custom' ? (v.cidr || '').trim()
        : ({ any: '', any4: '0.0.0.0/0', any6: '::/0' }[v.source] ?? '');
    return {
        direction: v.direction,
        protocol: v.protocol,
        family,
        action: v.action,
        priority: v.priority,
        description: v.description,
        port_range_min: portless ? null : (v.port_start ?? null),
        port_range_max: portless ? null : (v.port_end ?? v.port_start ?? null),
        remote_ip_prefix: remote,
    };
};

/** Quick rules: both families; ping = ICMP + ICMPv6. */
const tcp = (port, d) => [{ direction: 'ingress', protocol: 'tcp', family: 'both', port_range_min: port, port_range_max: port, remote_ip_prefix: '', action: 'accept', description: d }];
export const SG_TEMPLATES = {
    SSH: tcp(22, 'SSH'),
    HTTP: tcp(80, 'HTTP'),
    HTTPS: tcp(443, 'HTTPS'),
    RDP: tcp(3389, 'RDP'),
    Ping: [
        { direction: 'ingress', protocol: 'icmp', family: 'ipv4', remote_ip_prefix: '0.0.0.0/0', action: 'accept', description: 'Ping' },
        { direction: 'ingress', protocol: 'icmpv6', family: 'ipv6', remote_ip_prefix: '::/0', action: 'accept', description: 'Ping (IPv6)' },
    ],
};

export const ruleFamily = (r) => r.family || (String(r.remote_ip_prefix || '').includes(':') && r.remote_ip_prefix !== '::/0' ? 'ipv6' : 'both');

export const FamilyTag = ({ rule }) => {
    const { t } = useTranslation();
    const f = ruleFamily(rule);
    return <Tag color={f === 'ipv6' ? 'purple' : f === 'ipv4' ? 'blue' : 'default'}>{f === 'both' ? t('Dual-stack') : f === 'ipv4' ? 'IPv4' : 'IPv6'}</Tag>;
};

export const protocolLabel = (p, t) => (p === 'icmpv6' ? 'ICMPv6' : p && p !== 'all' ? p.toUpperCase() : t('All'));

export const remoteLabel = (r, t) => {
    const cidr = r.remote_ip_prefix || '';
    if (cidr === '' || ruleFamily(r) === 'both') return t('Any');
    if (cidr === '0.0.0.0/0' || cidr === '::/0') return t('Any') + ` (${cidr})`;
    return cidr;
};

export const portsLabel = (r, t) => {
    if (PORTLESS.includes(r.protocol)) return '-';
    const min = r.port_range_min;
    const max = r.port_range_max;
    if (min == null && max == null) return t('All');
    if (min === max) return min;
    return `${min ?? 1}-${max ?? 65535}`;
};

const isV6 = (s) => String(s || '').includes(':');
const cidrOk = (s) => /^[0-9a-fA-F.:]+(\/\d{1,3})?$/.test(String(s || '').trim());

/** Protocol, ports, family, source and action fields (inside a Form). */
const SgRuleFields = ({ form }) => {
    const { t } = useTranslation();
    const protocol = Form.useWatch('protocol', form);
    const family = Form.useWatch('family', form);
    const source = Form.useWatch('source', form);
    const v6only = protocol === 'icmpv6';

    const setFamily = (f) => {
        const src = form.getFieldValue('source');
        // keep a custom CIDR only when it fits the new family
        if (src !== 'custom' || f === 'both') form.setFieldsValue({ source: ANY[f] });
    };
    const setSource = (s) => {
        if (s !== 'custom') form.setFieldsValue({ family: { any: 'both', any4: 'ipv4', any6: 'ipv6' }[s] });
        else if (form.getFieldValue('family') === 'both') form.setFieldsValue({ family: 'ipv4' });
    };

    return (
        <>
            <Form.Item name="direction" label={t('Direction')} rules={[{ required: true }]}>
                <Select options={[{ value: 'ingress', label: t('Inbound') }, { value: 'egress', label: t('Outbound') }]} />
            </Form.Item>
            <Form.Item name="protocol" label={t('Protocol')} rules={[{ required: true }]}>
                <Select
                    onChange={(p) => { if (p === 'icmpv6') form.setFieldsValue({ family: 'ipv6', source: form.getFieldValue('source') === 'custom' ? 'custom' : 'any6' }); }}
                    options={[
                        { value: 'tcp', label: 'TCP' }, { value: 'udp', label: 'UDP' },
                        { value: 'icmp', label: 'ICMP' }, { value: 'icmpv6', label: 'ICMPv6' },
                        { value: 'all', label: t('All') },
                    ]}
                />
            </Form.Item>
            {!PORTLESS.includes(protocol) && (
                <Space style={{ display: 'flex' }} align="baseline">
                    <Form.Item name="port_start" label={t('Start port')}>
                        <InputNumber min={1} max={65535} placeholder="1" />
                    </Form.Item>
                    <Form.Item name="port_end" label={t('End port')}>
                        <InputNumber min={1} max={65535} placeholder="65535" />
                    </Form.Item>
                </Space>
            )}
            <Form.Item name="family" label={t('Address families')}>
                <Radio.Group optionType="button" onChange={(e) => setFamily(e.target.value)} options={[
                    { value: 'both', label: t('Dual-stack'), disabled: v6only },
                    { value: 'ipv4', label: 'IPv4', disabled: v6only },
                    { value: 'ipv6', label: 'IPv6' },
                ]} />
            </Form.Item>
            <Form.Item name="source" label={t('Source/destination')} tooltip={t('Inbound uses the source CIDR; outbound uses the destination CIDR')}>
                <Select onChange={setSource} options={[
                    { value: 'any', label: t('Any'), disabled: v6only },
                    { value: 'any4', label: t('Any IPv4 (0.0.0.0/0)'), disabled: v6only },
                    { value: 'any6', label: t('Any IPv6 (::/0)') },
                    { value: 'custom', label: t('Custom CIDR') },
                ]} />
            </Form.Item>
            {source === 'custom' && (
                <Form.Item
                    name="cidr"
                    rules={[
                        { required: true, message: t('Enter a CIDR') },
                        {
                            validator: (_, v) => (!v || (cidrOk(v) && isV6(v) === (family === 'ipv6'))
                                ? Promise.resolve()
                                : Promise.reject(new Error(family === 'ipv6' ? t('Enter an IPv6 CIDR') : t('Enter an IPv4 CIDR')))),
                        },
                    ]}
                >
                    <Input placeholder={family === 'ipv6' ? '2001:db8::/32' : '203.0.113.0/24'} />
                </Form.Item>
            )}
            <Form.Item name="action" label={t('Policy')} rules={[{ required: true }]}>
                <Select options={[{ value: 'accept', label: t('Allow') }, { value: 'drop', label: t('Deny') }]} />
            </Form.Item>
        </>
    );
};

export default SgRuleFields;
